// frontend/src/hooks/useAutoRefresh.js
// Spec: "Dashboard auto-refresh every 15 minutes."
import { useEffect, useRef, useState, useCallback } from 'react';

const DEFAULT_MS = Number(process.env.REACT_APP_REFRESH_MS) || 15 * 60 * 1000;

/**
 * Calls `callback` on mount and then every `intervalMs`.
 * Skips the tick while the tab is hidden and catches up when it becomes
 * visible again, so a backgrounded tab doesn't hammer the API.
 *
 * Returns { lastRefresh, refreshing, refresh } for the "Refresh" button.
 */
export default function useAutoRefresh(callback, intervalMs = DEFAULT_MS) {
    const [lastRefresh, setLastRefresh] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const cbRef = useRef(callback);
    const missedRef = useRef(false);

    // Keep the latest callback without restarting the timer.
    useEffect(() => { cbRef.current = callback; }, [callback]);

    const refresh = useCallback(async () => {
        setRefreshing(true);
        try {
            await cbRef.current();
            setLastRefresh(new Date());
        } finally {
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        refresh();

        const tick = () => {
            if (document.hidden) {
                missedRef.current = true;
                return;
            }
            refresh();
        };
        const id = setInterval(tick, intervalMs);

        const onVisible = () => {
            if (!document.hidden && missedRef.current) {
                missedRef.current = false;
                refresh();
            }
        };
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intervalMs]);

    return { lastRefresh, refreshing, refresh };
}
